import { debugLog } from './debugLog';
import localforage from "localforage";
import {
  debouncedPush,
  pushKey,
  pushAll,
  pullAll,
  cancelAllPending,
  cancelPendingForKey,
  deleteAll,
  flushPending,
  hasPendingPushes,
  setSyncCallbacks,
} from "./cloudSync";

localforage.config({
  name: "PensionAppDB",
  storeName: "pension_store"
});

const STORAGE_KEYS = [
  "pension_clients_v1",
  "pension_plans_v1",
  "pension_snapshot_v1",
  "pension_advisor_logo_v1",
  "pension_global_matrix_v1",
  "pension_general_notes_v1",
  "pension_activity_log_v1",
  "pension_relationships_v1",
  "pension_holdings_v1",
  "pension_holdings_history_v1",
  "pension_hachshara_v1",
  "pension_harel_v1",
  "pension_menora_v1",
  "pension_clal_v2",
  "pension_portfolio_map_v1",
  "pension_client_overrides_v1",
  "pension_settings_v1",
  "pension_license_photo_v1",
  "pension_license_sig_v1",
  "pension_report_log_v1",
  "pension_fund_notes_v1"
];

const META_KEY  = "__pension_sync_meta_v1";
const OWNER_KEY = "__pension_owner_uid_v1"; // which user's data is in this IndexedDB

export const memoryStore = {
  data: {},
  meta: {},
  _cloudEnabled: false,
  _lastSync: null,
  _syncStatus: 'idle',
  _lastError: null,
  _onSyncChange: null,

  getItem(key) {
    return this.data[key] || null;
  },

  setItem(key, value) {
    this.data[key] = value;
    if (STORAGE_KEYS.includes(key)) {
      const now = new Date().toISOString();
      this.meta[key] = now;
      localforage.setItem(key, value).catch(err => {
        console.error("IndexedDB save failed for", key, err);
      });
      localforage.setItem(META_KEY, this.meta).catch(err => {
        console.error("IndexedDB meta save failed", err);
      });
      if (this._cloudEnabled) {
        debouncedPush(key, value);
      }
    }
  },

  removeItem(key) {
    delete this.data[key];
    if (STORAGE_KEYS.includes(key)) {
      delete this.meta[key];
      localforage.removeItem(key).catch(err => {
        console.error("IndexedDB remove failed for", key, err);
      });
      localforage.setItem(META_KEY, this.meta).catch(() => {});
    }
  },

  _setSyncError(err) {
    this._syncStatus = 'error';
    this._lastError = err ? (err.message || String(err)) : 'Unknown sync error';
    this._onSyncChange?.();
  },

  _setSyncOk() {
    this._syncStatus = 'idle';
    this._lastError = null;
    this._lastSync = new Date();
    this._onSyncChange?.();
  },

  _setSyncing() {
    this._syncStatus = 'syncing';
    this._onSyncChange?.();
  },
};

setSyncCallbacks({
  onError: (err) => memoryStore._setSyncError(err),
  onSuccess: () => memoryStore._setSyncOk(),
});

// Like setItem but pushes to cloud immediately (no debounce).
// Use for explicit user actions (e.g. adding/editing/deleting a note)
// where we want the data in Supabase before the user can close the tab.
export function setItemImmediate(key, value) {
  memoryStore.data[key] = value;
  if (!STORAGE_KEYS.includes(key)) return;
  const now = new Date().toISOString();
  memoryStore.meta[key] = now;
  localforage.setItem(key, value).catch(err => {
    console.error("IndexedDB save failed for", key, err);
  });
  localforage.setItem(META_KEY, memoryStore.meta).catch(() => {});
  if (memoryStore._cloudEnabled) {
    cancelPendingForKey(key); // cancel any outstanding debounce for this key
    memoryStore._setSyncing();
    pushKey(key, value)
      .then(() => memoryStore._setSyncOk())
      .catch(err => memoryStore._setSyncError(err));
  }
}

export async function enableCloudSync() {
  memoryStore._cloudEnabled = true;
  memoryStore._setSyncing();
  try {
    await pushAll(memoryStore.data, STORAGE_KEYS);
    memoryStore._setSyncOk();
  } catch (err) {
    console.error('[Storage] enableCloudSync push failed:', err);
    memoryStore._setSyncError(err);
  }
}

export async function disableCloudSync(deleteRemote = false) {
  cancelAllPending();
  memoryStore._cloudEnabled = false;
  memoryStore._syncStatus = 'idle';
  memoryStore._lastError = null;
  if (deleteRemote) {
    try {
      await deleteAll();
    } catch (err) {
      console.error('[Storage] deleteAll failed:', err);
    }
  }
  memoryStore._onSyncChange?.();
}

function mergeClientLists(localJson, remoteJson) {
  const tryParse = (v) => { try { return JSON.parse(v); } catch { return null; } };
  const local  = tryParse(localJson)  || [];
  const remote = tryParse(remoteJson) || [];

  const map = new Map();
  const clientKey = (c) => (c.clientId && c.clientId !== '—') ? c.clientId : c.id;

  for (const c of remote) map.set(clientKey(c), { ...c });

  for (const c of local) {
    const k = clientKey(c);
    if (!map.has(k)) {
      map.set(k, { ...c }); // local-only client — keep it
    } else {
      const rem = map.get(k);
      // Keep whichever has more datasets; datasets are the source of truth for a client
      if ((c.datasets?.length || 0) > (rem.datasets?.length || 0)) {
        map.set(k, { ...c });
      }
    }
  }

  return JSON.stringify(Array.from(map.values()));
}

export async function syncFromCloud() {
  memoryStore._setSyncing();
  try {
    const remote = await pullAll();
    for (const key of STORAGE_KEYS) {
      const remoteEntry = remote[key];
      const hasRemote = remoteEntry != null;
      const hasLocal = memoryStore.data[key] != null;
      const localTimeStr = memoryStore.meta[key] || null;
      const localTime = localTimeStr ? new Date(localTimeStr).getTime() : null;
      const remoteTime = remoteEntry?.updated_at ? new Date(remoteEntry.updated_at).getTime() : null;

      if (!hasLocal && !hasRemote) continue;

      if (hasRemote && !hasLocal) {
        memoryStore.data[key] = remoteEntry.value;
        memoryStore.meta[key] = remoteEntry.updated_at;
        await localforage.setItem(key, remoteEntry.value);
        continue;
      }

      if (hasLocal && !hasRemote) {
        await pushKey(key, memoryStore.data[key]);
        if (!memoryStore.meta[key]) {
          memoryStore.meta[key] = new Date().toISOString();
        }
        continue;
      }

      // both exist
      if (key === 'pension_clients_v1') {
        // Clients are never replaced wholesale — merge so no client is ever silently lost.
        // Winner-takes-all on timestamps caused accidental data loss when one side had
        // a newer timestamp but fewer clients (e.g. unsynced local additions).
        const merged = mergeClientLists(memoryStore.data[key], remoteEntry.value);
        const changed = merged !== memoryStore.data[key];
        memoryStore.data[key] = merged;
        memoryStore.meta[key] = new Date().toISOString();
        await localforage.setItem(key, merged);
        if (changed) {
          // Push merged result so both sides converge
          await pushKey(key, merged);
        }
      } else if (localTime == null) {
        // Migration: no local meta. Prefer local (the data the user is actively working with)
        // and push it to cloud. Prevents wiping unsynced local data on first run after upgrade.
        debugLog('[Storage] migration: no local meta for', key, '— pushing local to cloud');
        await pushKey(key, memoryStore.data[key]);
        memoryStore.meta[key] = new Date().toISOString();
      } else if (remoteTime != null && remoteTime > localTime) {
        memoryStore.data[key] = remoteEntry.value;
        memoryStore.meta[key] = remoteEntry.updated_at;
        await localforage.setItem(key, remoteEntry.value);
      } else if (localTime > (remoteTime || 0)) {
        await pushKey(key, memoryStore.data[key]);
      }
    }
    await localforage.setItem(META_KEY, memoryStore.meta);
    memoryStore._setSyncOk();
    debugLog('[Storage] syncFromCloud complete');
    // Notify all hooks that read from memoryStore to re-read their data
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pension-data-synced'));
    }
  } catch (err) {
    console.error('[Storage] syncFromCloud failed:', err);
    memoryStore._setSyncError(err);
  }
}

export async function retrySync() {
  if (!memoryStore._cloudEnabled) return;
  memoryStore._setSyncing();
  try {
    await pushAll(memoryStore.data, STORAGE_KEYS);
    memoryStore._setSyncOk();
  } catch (err) {
    console.error('[Storage] retrySync failed:', err);
    memoryStore._setSyncError(err);
  }
}

function handleOnline() {
  if (memoryStore._cloudEnabled) {
    debugLog('[Storage] Back online — pushing all data');
    memoryStore._setSyncing();
    pushAll(memoryStore.data, STORAGE_KEYS)
      .then(() => memoryStore._setSyncOk())
      .catch(err => memoryStore._setSyncError(err));
  }
}

function handleBeforeUnload() {
  if (memoryStore._cloudEnabled && hasPendingPushes()) {
    flushPending();
  }
}

function handleVisibilityChange() {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden' && memoryStore._cloudEnabled && hasPendingPushes()) {
    flushPending();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', handleOnline);
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('pagehide', handleBeforeUnload);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
}

// Called once before React mounts
export async function initStorage() {
  try {
    for (const key of STORAGE_KEYS) {
      const val = await localforage.getItem(key);
      if (val !== null) {
        memoryStore.data[key] = val;
      }
    }
    const meta = await localforage.getItem(META_KEY);
    if (meta && typeof meta === 'object') {
      memoryStore.meta = meta;
    }
    debugLog("IndexedDB storage loaded successfully");
  } catch (err) {
    console.error("Failed to load storage from IndexedDB", err);
  }
}

// Clear all local data (IndexedDB + memoryStore) — must be called on sign-out
// so the next user who logs in starts with a clean slate.
export async function clearLocalData() {
  cancelAllPending();
  memoryStore._cloudEnabled = false;
  memoryStore.data = {};
  memoryStore.meta = {};
  memoryStore._syncStatus = 'idle';
  memoryStore._lastSync = null;
  memoryStore._lastError = null;
  memoryStore._onSyncChange?.();
  try {
    for (const key of STORAGE_KEYS) {
      await localforage.removeItem(key);
    }
    await localforage.removeItem(META_KEY);
    await localforage.removeItem(OWNER_KEY);
    debugLog('[Storage] local data cleared');
  } catch (err) {
    console.error('[Storage] clearLocalData failed:', err);
  }
}

// Call before syncFromCloud — if IndexedDB contains another user's data, clear it first.
// This guards against the case where the app starts with stale data in IndexedDB
// and a different user is now logged in.
export async function ensureLocalDataForUser(userId) {
  try {
    const storedUid = await localforage.getItem(OWNER_KEY);
    if (storedUid && storedUid !== userId) {
      debugLog('[Storage] IndexedDB owned by', storedUid, '— clearing for', userId);
      await clearLocalData();
    }
    await localforage.setItem(OWNER_KEY, userId);
  } catch (err) {
    console.error('[Storage] ensureLocalDataForUser failed:', err);
  }
}

// Export a backup of all keys as a JSON file
export async function exportWorkspace() {
  try {
    const backup = {};
    for (const key of STORAGE_KEYS) {
      const val = await localforage.getItem(key);
      if (val !== null) {
        backup[key] = val;
      }
    }

    const blob = new Blob([JSON.stringify(backup)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pension_workspace_backup_${new Date().toISOString().slice(0,10)}.pension`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    console.error("Export failed", err);
    return false;
  }
}

// Import a backup JSON file
export async function importWorkspace(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const backup = JSON.parse(e.target.result);
        const now = new Date().toISOString();
        for (const key of STORAGE_KEYS) {
          if (backup[key]) {
            await localforage.setItem(key, backup[key]);
            memoryStore.data[key] = backup[key];
            memoryStore.meta[key] = now;
          }
        }
        await localforage.setItem(META_KEY, memoryStore.meta);
        // Imported data is the new source of truth — push to cloud if enabled.
        if (memoryStore._cloudEnabled) {
          memoryStore._setSyncing();
          pushAll(memoryStore.data, STORAGE_KEYS)
            .then(() => memoryStore._setSyncOk())
            .catch(err => memoryStore._setSyncError(err));
        }
        resolve(true);
      } catch (err) {
        console.error("Invalid backup file", err);
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}
